import type {ReactElement} from 'react';
import {useRef, useState} from 'react';
import {SocialPlatform} from '@Components/social-media/social-media.types';
import {SocialThumbnail} from '@Components/social-media/components/social-thumbnail';
import useOutsideClick from '@Hooks/useOutsideClick';
import {ClickableDiv} from '@Components/clickable-div';
import {Text, TextSize} from '@Components/text';
import {noop} from '@Utils/general.util';
import styles from './social-account-button.module.scss';

interface SocialAccountButtonProps {
  type: SocialPlatform;
  isActive?: boolean;
  isSelected?: boolean;
  onClick?: VoidFunction;
}

export function SocialAccountButton({type, isActive = false, isSelected = false, onClick = noop}: SocialAccountButtonProps): ReactElement<any> {
  const [isButtonActive, setIsButtonActive] = useState(isActive);
  const [isClicked, setIsClicked] = useState(isActive);
  const ref = useRef(null);

  const getLabel = (): string => {
    switch (type) {
      case SocialPlatform.FACEBOOK:
      case SocialPlatform.FACEBOOK_PROFILE:
      case SocialPlatform.FACEBOOK_PAGE:
      case SocialPlatform.FACEBOOK_GROUP:
        return 'Facebook';
      case SocialPlatform.TWITTER:
        return 'X (formerly Twitter)';
      case SocialPlatform.LINKEDIN:
      case SocialPlatform.LINKEDIN_PROFILE:
      case SocialPlatform.LINKEDIN_PAGE:
        return 'LinkedIn';
      case SocialPlatform.TIKTOK:
        return 'TikTok';
      case SocialPlatform.YOUTUBE:
        return 'YouTube';
      case SocialPlatform.INSTAGRAM:
        return 'Instagram';
      case SocialPlatform.PINTEREST:
        return 'Pinterest';
      case SocialPlatform.GOOGLE_BUSINESS_PROFILE:
        return 'Google Business';
      case SocialPlatform.THREADS:
        return 'Threads';
      default:
        return '';
    }
  };

  const getDescription = (): string => {
    switch (type) {
      case SocialPlatform.FACEBOOK:
      case SocialPlatform.FACEBOOK_PROFILE:
      case SocialPlatform.FACEBOOK_PAGE:
      case SocialPlatform.FACEBOOK_GROUP:
        return window.i18next.t('pmwjs_pages');
      case SocialPlatform.TWITTER:
      case SocialPlatform.THREADS:
        return window.i18next.t('pmwjs_profiles');
      case SocialPlatform.LINKEDIN:
      case SocialPlatform.LINKEDIN_PROFILE:
      case SocialPlatform.LINKEDIN_PAGE:
        return window.i18next.t('pmwjs_accounts_pages');
      case SocialPlatform.TIKTOK:
      case SocialPlatform.PINTEREST:
        return window.i18next.t('pmwjs_accounts');
      case SocialPlatform.YOUTUBE:
        return window.i18next.t('pmwjs_shorts_and_videos');
      case SocialPlatform.INSTAGRAM:
        // Instagram for Business is a product name, won't be translated
        return 'Business Accounts';
      case SocialPlatform.GOOGLE_BUSINESS_PROFILE:
        return 'Business Profile';
      default:
        return '';
    }
  };
  const doSetActive = (): void => {
    setIsButtonActive(true);
  };

  const onOutsideClick = (): void => {
    setIsClicked(false);
  };

  const doSetInactive = (): void => {
    if (!isClicked) {
      setIsButtonActive(false);
    }
  };

  const onAccountButtonClick = (): void => {
    doSetActive();
    setIsClicked(true);
    onClick();
  };

  useOutsideClick(ref, onOutsideClick, ref.current);

  return (
    <ClickableDiv
      className={`${styles.accountButton} ${isClicked || isSelected ? styles.accountButtonSelected : ''} spacing-p-3 flex-row-align-center`}
      onMouseOver={doSetActive}
      onFocus={doSetActive}
      onBlur={doSetInactive}
      onMouseOut={doSetInactive}
      onClick={onAccountButtonClick}
      ref={ref}
    >
      <div>
        <SocialThumbnail type={type} isActive={isButtonActive || isSelected} />
      </div>
      <div className="flex-v-row spacing-m-l-2">
        <Text size={TextSize.MD} bold className="ct-neutral-primary" val={getLabel()} />
        <Text size={TextSize.SM} className="ct-neutral-secondary" val={getDescription()} />
      </div>
    </ClickableDiv>
  );
}
